/**
 * Speech Recording Hook
 * Custom hook that manages MediaRecorder API, state management, and Firebase Functions integration
 * Follows existing patterns from other hooks in the codebase
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useUntypedTranslation } from './useUntypedTranslation';
import {
  RecordingState,
  UseSpeechRecordingReturn,
  ProcessedRecordingDocument,
  RECORDING_CONFIG
} from '../types/speechRecording';
import {
  generateRecordingId,
  getSupportedMimeType,
  isMediaRecorderSupported,
  uploadAudioToStorage,
  createRecordingDocument,
  listenToRecordingDocument,
  getErrorTranslationKey,
  validateAudioBlob,
  getBrowserSupportInfo,
  stopRecordingAndWaitForData
} from '../services/speechRecordingService';

/**
 * Custom hook for speech recording functionality
 * Integrates with Firebase Functions trigger-based architecture
 */
export const useSpeechRecording = (): UseSpeechRecordingReturn => {
  // Get translation function
  const { t } = useUntypedTranslation();
  
  // Get current user from Redux (following existing patterns)
  const currentUser = useSelector((state: RootState) => state.user.user);
  const userId = currentUser?.id;

  // Recording state management
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [transcribedText, setTranscribedText] = useState<string | null>(null);
  // Store translation key, not localized string, so it updates on language change
  const [errorKey, setErrorKey] = useState<string | null>(null);
  // Derive localized error from key; this recomputes on language change via t
  const error = errorKey ? t(errorKey) : null;

  // Refs for MediaRecorder and timers
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const firestoreListenerRef = useRef<(() => void) | null>(null);
  const currentRecordingIdRef = useRef<string | null>(null);
  
  // Refs for microphone readiness detection
  const microphoneReadyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const micReadyHandledRef = useRef<boolean>(false);

  // Browser support check
  const isSupported = isMediaRecorderSupported();

  /**
   * Stops the recording timer
   */
  const stopRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    recordingStartTimeRef.current = 0;
  }, []);

  /**
   * Updates recording duration timer
   */
  const updateRecordingDuration = useCallback(() => {
    if (recordingStartTimeRef.current > 0) {
      const elapsed = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
      setRecordingDuration(elapsed);
      
      // Stop recording if max duration reached
      if (elapsed >= RECORDING_CONFIG.MAX_DURATION_MS / 1000) {
        console.log('[SpeechRecording] Max duration reached, stopping recording automatically');
        // Stop recording and show timeout error
        const timeoutError = { message: 'Recording stopped automatically' };
        setErrorKey(getErrorTranslationKey(timeoutError));
        setRecordingState('error');
        
        // Cleanup the recording
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          try {
            mediaRecorderRef.current.stop();
            const stream = mediaRecorderRef.current.stream;
            stream.getTracks().forEach(track => track.stop());
          } catch (stopError) {
            console.error('[SpeechRecording] Error stopping MediaRecorder on timeout:', stopError);
          }
        }
        
        // Clean up refs and timers
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        stopRecordingTimer();
      }
    }
  }, [stopRecordingTimer, t]);

  /**
   * Starts the recording timer
   */
  const startRecordingTimer = useCallback(() => {
    recordingStartTimeRef.current = Date.now();
    setRecordingDuration(0);
    
    recordingTimerRef.current = setInterval(updateRecordingDuration, 1000);
  }, [updateRecordingDuration]);

  /**
   * Cleans up current recording session
   */
  const cleanupRecording = useCallback(() => {
    // Stop and cleanup MediaRecorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    
    // Clear audio chunks
    audioChunksRef.current = [];
    
    // Stop timer
    stopRecordingTimer();
    
    // Cleanup microphone readiness detection
    if (microphoneReadyTimeoutRef.current) {
      clearTimeout(microphoneReadyTimeoutRef.current);
      microphoneReadyTimeoutRef.current = null;
    }
    micReadyHandledRef.current = false;
    
    // Cleanup Firestore listener
    if (firestoreListenerRef.current) {
      firestoreListenerRef.current();
      firestoreListenerRef.current = null;
    }
    
    // Clear recording ID
    currentRecordingIdRef.current = null;
    
    console.log('[SpeechRecording] Cleanup completed');
  }, [stopRecordingTimer]);

  /**
   * Handles microphone readiness detection
   */
  const handleMicrophoneReady = useCallback(() => {
    if (micReadyHandledRef.current) return;
    micReadyHandledRef.current = true;
    
    console.log('[SpeechRecording] Microphone ready, switching to recording state');
    
    // Clear the fallback timeout
    if (microphoneReadyTimeoutRef.current) {
      clearTimeout(microphoneReadyTimeoutRef.current);
      microphoneReadyTimeoutRef.current = null;
    }
    
    // Switch to recording state and start timer
    setRecordingState('recording');
    startRecordingTimer();
  }, [startRecordingTimer]);

  /**
   * Handles recording document updates from Firestore
   */
  const handleRecordingUpdate = useCallback((data: ProcessedRecordingDocument | null) => {
    if (!data || !currentRecordingIdRef.current) return;

    console.log(`[SpeechRecording] Document update:`, {
      recordingId: data.id,
      status: data.status,
      hasText: !!data.transcribedText
    });

    if (data.status === 'done' && data.transcribedText) {
      // Success: transcription complete
      setTranscribedText(data.transcribedText);
      setRecordingState('done');
      
      // Cleanup listener after success
      if (firestoreListenerRef.current) {
        firestoreListenerRef.current();
        firestoreListenerRef.current = null;
      }
    } else if (data.status === 'error') {
      // Error: transcription failed
      const errorMessage = data.error || 'Processing failed';
      setErrorKey(getErrorTranslationKey({ message: errorMessage }));
      setRecordingState('error');
      
      // Cleanup listener after error
      if (firestoreListenerRef.current) {
        firestoreListenerRef.current();
        firestoreListenerRef.current = null;
      }
    }
    // If status is still 'processing', keep waiting
  }, []);

  /**
   * Starts audio recording
   */
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setErrorKey('speechRecording.notSupported');
      setRecordingState('error');
      return;
    }

    if (!userId) {
      setErrorKey('speechRecording.microphoneAccess');
      setRecordingState('error');
      return;
    }

    if (recordingState !== 'idle') {
      console.warn('[SpeechRecording] Already recording or processing');
      return;
    }

    try {
      setErrorKey(null);
      setTranscribedText(null);
      setRecordingState('preparing'); // Start in preparing state
      
      // Log browser support info for debugging
      const supportInfo = getBrowserSupportInfo();
      console.log('[SpeechRecording] Starting recording...', supportInfo);

      // Request microphone access with fallback mechanism
      let stream: MediaStream;
      try {
        // Try with preferred constraints first
        stream = await navigator.mediaDevices.getUserMedia({
          audio: RECORDING_CONFIG.AUDIO_CONSTRAINTS
        });
      } catch (constraintError) {
        console.warn('[SpeechRecording] Preferred constraints failed, trying minimal constraints...', constraintError);
        // Fallback to minimal constraints for problematic devices
        stream = await navigator.mediaDevices.getUserMedia({
          audio: RECORDING_CONFIG.MINIMAL_AUDIO_CONSTRAINTS
        });
      }

      // Set up microphone readiness detection
      micReadyHandledRef.current = false;

      // Get supported MIME type
      const mimeType = getSupportedMimeType();
      
      // Create MediaRecorder with fallback options
      let mediaRecorder: MediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType,
          audioBitsPerSecond: RECORDING_CONFIG.AUDIO_BITS_PER_SECOND
        });
      } catch (mimeError) {
        console.warn(`[SpeechRecording] Failed to create MediaRecorder with ${mimeType}, trying without options:`, mimeError);
        // Fallback: create MediaRecorder without specific options
        mediaRecorder = new MediaRecorder(stream);
      }

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Set up MediaRecorder event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('[SpeechRecording] MediaRecorder error:', event);
        setErrorKey('speechRecording.error');
        setRecordingState('error');
        cleanupRecording();
      };

      // Set up MediaRecorder start event - most reliable way to detect recording readiness
      mediaRecorder.onstart = () => {
        console.log('[SpeechRecording] MediaRecorder started, allowing hardware warmup');
        // Short delay to allow microphone hardware to stabilize
        microphoneReadyTimeoutRef.current = setTimeout(() => {
          console.log('[SpeechRecording] Hardware warmup complete, microphone ready');
          handleMicrophoneReady();
        }, RECORDING_CONFIG.MICROPHONE_READY_DELAY_MS);
      };

      // Start recording - onstart event will handle the readiness detection
      mediaRecorder.start(1000); // Collect data every second
      
      console.log(`[SpeechRecording] MediaRecorder.start() called, waiting for onstart event:`, {
        requestedMimeType: mimeType,
        actualMimeType: mediaRecorder.mimeType,
        audioBitsPerSecond: RECORDING_CONFIG.AUDIO_BITS_PER_SECOND
      });

    } catch (error: unknown) {
      console.error('[SpeechRecording] Failed to start recording:', error);
      
      if (error && typeof error === 'object' && 'name' in error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          setErrorKey('speechRecording.permissionDenied');
        } else if (error.name === 'NotFoundError') {
          setErrorKey('speechRecording.noMicrophone');
        } else if (error.name === 'NotSupportedError') {
          setErrorKey('speechRecording.notSupported');
        } else {
          setErrorKey('speechRecording.error');
        }
      } else {
        setErrorKey('speechRecording.error');
      }
      
      setRecordingState('error');
      cleanupRecording();
    }
  }, [isSupported, userId, recordingState, cleanupRecording, startRecordingTimer, handleMicrophoneReady, t]);

  /**
   * Stops audio recording and initiates upload/transcription process
   */
  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || (recordingState !== 'recording' && recordingState !== 'preparing')) {
      console.warn('[SpeechRecording] No active recording to stop');
      return;
    }

    if (!userId) {
      setErrorKey('speechRecording.microphoneAccess');
      setRecordingState('error');
      return;
    }

    try {
      console.log('[SpeechRecording] Stopping recording...');
      
      // Stop timer
      stopRecordingTimer();
      
      // Clear microphone ready timeout if still pending
      if (microphoneReadyTimeoutRef.current) {
        clearTimeout(microphoneReadyTimeoutRef.current);
        microphoneReadyTimeoutRef.current = null;
      }
      
      // Use the smart stopping function that handles cross-browser differences
      const mediaRecorder = mediaRecorderRef.current;
      const audioBlob = await stopRecordingAndWaitForData(mediaRecorder, audioChunksRef.current);
      
      console.log(`[SpeechRecording] Audio blob created:`, {
        size: audioBlob.size,
        type: audioBlob.type,
        duration: recordingDuration
      });

      // Validate audio blob
      const validation = validateAudioBlob(audioBlob);
      if (!validation.isValid) {
        setErrorKey(validation.errorKey || 'speechRecording.error');
        setRecordingState('error');
        cleanupRecording();
        return;
      }

      // Start upload process
      setRecordingState('uploading');
      
      // Generate recording ID
      const recordingId = generateRecordingId();
      currentRecordingIdRef.current = recordingId;
      
      console.log(`[SpeechRecording] Generated recording ID: ${recordingId}`);

      // Upload to Firebase Storage
      const storagePath = await uploadAudioToStorage(userId, recordingId, audioBlob);

      // Create Firestore document
      await createRecordingDocument(userId, recordingId, storagePath, mediaRecorder);

      // Set up Firestore listener
      firestoreListenerRef.current = listenToRecordingDocument(
        userId,
        recordingId,
        handleRecordingUpdate
      );

      // Start backend processing - Firebase Function automatically triggers
      setRecordingState('processing');
      
      console.log('[SpeechRecording] Upload completed, Firebase Function will process automatically');

    } catch (error: unknown) {
      console.error('[SpeechRecording] Failed to stop recording:', error);
      setErrorKey(getErrorTranslationKey(error));
      setRecordingState('error');
      cleanupRecording();
    }
  }, [
    recordingState, 
    userId, 
    recordingDuration, 
    stopRecordingTimer, 
    cleanupRecording, 
    handleRecordingUpdate,
    t
  ]);

  /**
   * Clears current error state
   */
  const clearError = useCallback(() => {
    setErrorKey(null);
    if (recordingState === 'error') {
      setRecordingState('idle');
    }
  }, [recordingState]);

  /**
   * Clears transcribed text after it has been delivered to parent component
   * This allows users to freely edit the text without interference
   */
  const clearTranscribedText = useCallback(() => {
    setTranscribedText(null);
  }, []);

  /**
   * Cleanup on component unmount
   */
  useEffect(() => {
    return () => {
      cleanupRecording();
    };
  }, [cleanupRecording]);

  /**
   * Auto-reset to idle state after successful transcription
   */
  useEffect(() => {
    if (recordingState === 'done') {
      const timer = setTimeout(() => {
        setRecordingState('idle');
        setRecordingDuration(0);
      }, 2000); // Show success state for 2 seconds

      return () => clearTimeout(timer);
    }
  }, [recordingState]);

  return {
    // State
    recordingState,
    isRecording: recordingState === 'recording',
    isPreparing: recordingState === 'preparing',
    recordingDuration,
    transcribedText,
    error,
    
    // Actions
    startRecording,
    stopRecording,
    clearError,
    clearTranscribedText,
    
    // Browser support
    isSupported
  };
};
